// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
/**
 * AnimationClip manages a set of animation channels as a single clip entity.
 */
class AnimationClip {
  /**
   * @param {string} name Name of clip
   * @param {Array<AbstractAnimationChannel>} [channels=[]] an array of channels to shallow copy locally.
   */
  constructor(name, channels) {
    this._name = name;
    this._channels = channels || [];
    this._maxTime = -1;
    this._isAdditive = false;

    this.updateMaxTimeIndex();
  }

  set name(newName) {
    this._name = newName;
  }

  set isAdditive(newValue) {
    this._isAdditive = newValue;
  }

  /*
   * Update an instance of this clip.
   * @param {number} clockTime the current local clip time (where 0 == start of clip)
   * @param {AnimationClipInstance} instance the instance record to update.
   */
  update(clockTime, instance) {
    // Go through each channel and update clipState
    for (let i = 0, max = this._channels.length; i < max; ++i) {
      const channel = this._channels[i];
      const applyTo = instance.getApplyTo(channel);
      channel.updateSample(clockTime, applyTo);
    }
  }

  /**
   * Add a channel to this clip.
   * @param {AbstractAnimationChannel} channel the channel to add.
   */
  addChannel(channel) {
    this._channels.push(channel);
    this.updateMaxTimeIndex();
  }

  /**
   * Remove a given channel from this clip.
   * @param {AbstractAnimationChannel} channel the channel to remove.
   * @returns {boolean} true if this clip had the given channel and it was removed.
   */
  removeChannel(channel) {
    const idx = this._channels.indexOf(channel);
    if (idx >= 0) {
      this._channels.splice(idx, 1);
      this.updateMaxTimeIndex();
      return true;
    }
    return false;
  }

  /**
   * Locate a channel in this clip using its channel name.
   * @param {string} channelName the name to match against.
   * @returns {AbstractAnimationChannel} the first channel with a name matching the given channelName, or null if no matches are found.
   */
  findChannelByName(channelName) {
    for (let i = 0, max = this._channels.length; i < max; ++i) {
      const channel = this._channels[i];
      if (channelName === channel._channelName) {
        return channel;
      }
    }
    return null;
  }

  /*
   * Update our max time value to match the max time in our managed animation channels.
   */
  updateMaxTimeIndex() {
    this._maxTime = -1;
    let max;
    for (let i = 0; i < this._channels.length; i++) {
      const channel = this._channels[i];
      max = channel.getMaxTime();
      if (max > this._maxTime) {
        this._maxTime = max;
      }
    }
  }

  toString() {
    return `${this._name}: ${
       this._channels.map(channel => channel._channelName)}`;
  }
}

export default AnimationClip;
